import { useEffect, useLayoutEffect, useState, type RefObject } from "react";

/**
 * Mesure la position et la largeur de l'élément actif d'un groupe, pour y faire
 * glisser un indicateur partagé.
 *
 * Il n'y a pas d'autre moyen que de mesurer : la largeur d'un onglet dépend de
 * la longueur de son libellé, donc du rendu effectif.
 *
 * Pourquoi un indicateur qui GLISSE plutôt qu'un soulignement par élément qu'on
 * allume et qu'on éteint : le déplacement continu relie visuellement l'ancienne
 * sélection à la nouvelle, là où deux fondus laissent l'œil chercher où regarder.
 *
 * @param containerRef conteneur positionné qui sert de repère
 * @param activeKey    valeur sélectionnée — déclenche une nouvelle mesure
 * @param selector     sélecteur CSS de l'élément actif
 */
export function useSlidingIndicator(
  containerRef: RefObject<HTMLElement | null>,
  activeKey: string,
  selector: string,
) {
  // `container` accompagne left/width : c'est ce qui permet aux appelants
  // d'exprimer la largeur de l'indicateur en `scaleX` — donc en transformation
  // composée par le GPU — plutôt qu'en pixels de mise en page.
  const [rect, setRect] = useState<{ left: number; width: number; container: number } | null>(
    null,
  );

  // `useLayoutEffect` et non `useEffect` : la mesure doit précéder la peinture,
  // sinon l'indicateur apparaît une frame en position 0 puis saute à sa place.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => {
      const active = container.querySelector<HTMLElement>(selector);
      if (!active) return setRect(null);

      // Différence de rectangles plutôt qu'`offsetLeft` : ce dernier se compte
      // depuis le bord de padding du parent, ce qui décalerait l'indicateur de
      // la valeur du padding sur les variantes qui en ont un.
      const containerBox = container.getBoundingClientRect();
      const activeBox = active.getBoundingClientRect();
      setRect({
        left: activeBox.left - containerBox.left,
        width: activeBox.width,
        container: containerBox.width,
      });
    };

    measure();

    // Les largeurs changent au redimensionnement et au chargement des polices ;
    // sans observateur, l'indicateur se décale silencieusement.
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    for (const child of container.children) observer.observe(child);

    return () => observer.disconnect();
  }, [containerRef, activeKey, selector]);

  // La toute première pose ne doit pas être animée, sinon l'indicateur glisse
  // depuis le bord gauche au montage.
  const [animated, setAnimated] = useState(false);
  useEffect(() => {
    if (rect) setAnimated(true);
  }, [rect]);

  return { rect, animated };
}
